import { useState } from "react";

import Button from "../components/layout/Button";
import LoadingSpinner from "../components/layout/LoadingSpinner";
import FormError from "../components/salesDocuments/FormError";
import { lookupSingleTps } from "../services/tps.service";
import { SingleTpsLookupResponse } from "../types/services.types";

const DISALLOWED_CHARS = new Set([
  " ",
  "-",
  "(",
  ")",
  ".",
  "{",
  "}",
  "[",
  "]",
  "/",
  "\\",
  "|",
  "*",
  "#",
  "!",
  "@",
  "$",
  "%",
  "^",
  "&",
  "=",
  "~",
  "`",
]);

function transformPhoneNumber(raw: string): string {
  let n = raw.trim().replace(/ /g, "");
  for (const char of DISALLOWED_CHARS) {
    n = n.split(char).join("");
  }
  if (n.startsWith("+44")) n = "0" + n.slice(3);
  if (n.startsWith("0044")) n = "0" + n.slice(4);
  if (n.startsWith("44") && n.length === 12) n = "0" + n.slice(2);
  if (!n.startsWith("0") && n.length === 10) n = "0" + n;
  if (!/^\d+$/.test(n)) throw new Error("non-digit");
  if (n.startsWith("0") && n.length === 11) return n;
  if (n.startsWith("01") && n.length === 10) return n;
  throw new Error("invalid format");
}

function isValidPhoneNumber(value: string): boolean {
  if (!value) return false;
  let transformed: string;
  try {
    transformed = transformPhoneNumber(value.trim());
  } catch {
    return false;
  }
  const len = transformed.length;
  if (len === 10) return /^01\d{8}$/.test(transformed);
  if (len === 11) return /^0[1,2,3,5,7,8,9]\d{9}$/.test(transformed);
  return true;
}

const SingleTpsLookup = () => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneNumberError, setPhoneNumberError] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [response, setResponse] = useState<SingleTpsLookupResponse | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validatePhoneNumber = (value: string) => {
    const valid = isValidPhoneNumber(value);
    setPhoneNumberError(!valid);
    return valid;
  };

  const handleSubmit = async () => {
    setRequestError(null);

    if (!validatePhoneNumber(phoneNumber)) {
      setResponse(null);
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await lookupSingleTps(phoneNumber);
      setResponse(result);
    } catch {
      setResponse(null);
      setRequestError("Unable to complete TPS lookup. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <h1>Check TPS Registration</h1>

      <div className="meter-search-container">
        <fieldset className="docs-form__section">
          <div className="docs-form__customer-field">
            <div className="docs-form__inputs">
              <label htmlFor="single-tps-phone-number">Phone number</label>
              <input
                id="single-tps-phone-number"
                type="text"
                value={phoneNumber}
                onChange={(event) => {
                  setPhoneNumber(event.target.value);
                  setPhoneNumberError(false);
                  setRequestError(null);
                }}
                onBlur={() => validatePhoneNumber(phoneNumber)}
                placeholder="Enter phone number"
              />
            </div>

            <div className="docs-form__error-messages">
              {phoneNumberError && <FormError text="Please enter a valid telephone number." />}
              {requestError && <FormError text={requestError} />}
            </div>
          </div>

          <div className="meter-details-footer">
            {isSubmitting ? (
              <LoadingSpinner message="Checking..." size="button" />
            ) : (
              <Button type="button" onClick={() => void handleSubmit()} disabled={isSubmitting}>
                Submit
              </Button>
            )}
          </div>
        </fieldset>

        {response && (
          <fieldset className="docs-form__section">
            <div className="docs-form__customer-field">
              <div className="docs-form__inputs">
                <label htmlFor="single-tps-response">Lookup result</label>
                <p>
                  Phone Number: <strong>{response.phone_number}</strong>
                </p>
                <p>
                  TPS Match: <strong>{response.tps_match ? "Yes" : "No"}</strong>
                </p>
                <p>
                  Message: <strong>{response.message}</strong>
                </p>
              </div>
            </div>
          </fieldset>
        )}
      </div>
    </div>
  );
};

export default SingleTpsLookup;
